import type { TurnBlock } from '../turnModel';

export interface WorkSummaryData {
  headline: string;
  stepCount: number;
  hasFailure: boolean;
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function categoryForActivity(tool: string) {
  const normalized = tool.toLowerCase();

  if (
    normalized.includes('artifact') ||
    normalized.includes('canvas') ||
    normalized.includes('slide')
  ) {
    return 'artifact';
  }

  if (normalized.includes('document') || normalized.includes('chapter')) {
    return 'document';
  }

  if (
    normalized.includes('search') ||
    normalized.includes('scrape') ||
    normalized.includes('url') ||
    normalized.includes('source') ||
    normalized.includes('linear') ||
    normalized.includes('github')
  ) {
    return 'research';
  }

  return 'action';
}

export function formatWorkDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  return minutes < 60 ? `${minutes}m ${total % 60}s` : `${Math.floor(minutes / 60)}h ${minutes % 60}m ${total % 60}s`;
}

export function summarizeWorkBlocks(blocks: TurnBlock[], touchedArtifacts?: number): WorkSummaryData {
  const workBlocks = blocks.filter(
    (block) =>
      block.kind === 'thinking' || block.kind === 'activity' || block.kind === 'task',
  );
  const counts = new Map<string, number>();

  for (const block of workBlocks) {
    const category =
      block.kind === 'activity' ? categoryForActivity(block.tool) : block.kind;
    counts.set(category, (counts.get(category) ?? 0) + 1);
  }

  const facts: string[] = [];
  const artifactCount = touchedArtifacts ?? counts.get('artifact') ?? 0;
  const documentCount = counts.get('document') ?? 0;
  const researchCount = counts.get('research') ?? 0;
  const actionCount = counts.get('action') ?? 0;
  const thinkingCount = counts.get('thinking') ?? 0;
  const taskCount = counts.get('task') ?? 0;

  if (artifactCount > 0) facts.push(pluralize(artifactCount, 'artifact'));
  if (documentCount > 0) facts.push(pluralize(documentCount, 'document'));
  if (researchCount > 0) facts.push(pluralize(researchCount, 'research step'));
  if (actionCount > 0) facts.push(pluralize(actionCount, 'action'));
  if (taskCount > 0) facts.push(pluralize(taskCount, 'task'));
  if (thinkingCount > 0 && facts.length === 0) {
    facts.push(pluralize(thinkingCount, 'thought'));
  }

  return {
    headline: facts.join(' · ') || 'No recorded work',
    stepCount: workBlocks.length,
    hasFailure: workBlocks.some(
      (block) => block.kind === 'activity' && block.lifecycle === 'failed',
    ),
  };
}
